import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook for real-time syncing of booking information for a user
 */
type BookingRealtimeOptions = {
  userId: string | null | undefined;
  type: 'client' | 'practitioner';
  onBookingUpdate: (booking: any) => void;
  onBookingInsert?: (booking: any) => void;
  onBookingDelete?: (booking: any) => void;
};

export function useBookingRealtimeSync({ 
  userId, 
  type, 
  onBookingUpdate,
  onBookingInsert,
  onBookingDelete
}: BookingRealtimeOptions) {
  const channelRef = useRef<any>(null);
  const isSubscribed = useRef(false);

  // Use refs for callbacks to avoid effect re-runs on every render
  const onUpdateRef = useRef(onBookingUpdate);
  const onInsertRef = useRef(onBookingInsert);
  const onDeleteRef = useRef(onBookingDelete);

  // Keep refs in sync
  onUpdateRef.current = onBookingUpdate;
  onInsertRef.current = onBookingInsert;
  onDeleteRef.current = onBookingDelete;

  useEffect(() => {
    if (!userId) return;

    // Build a unique channel name including type and which events are listened to
    const eventKey = [
      'u',
      onInsertRef.current ? 'i' : '',
      onDeleteRef.current ? 'd' : '',
    ].filter(Boolean).join('');
    const channelName = `bookings-rt-${userId}-${type}-${eventKey}`;

    // Prevent duplicate subscriptions for the same channel
    if (isSubscribed.current && channelRef.current) {
      return;
    }

    const filter = type === 'client'
      ? `client_id=eq.${userId}`
      : `practitioner_id=eq.${userId}`;

    try {
      // Clean up any existing channel first
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            if (payload.new) {
              onUpdateRef.current(payload.new);
            }
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            if (payload.new) {
              if (onInsertRef.current) {
                onInsertRef.current(payload.new);
              } else {
                onUpdateRef.current(payload.new);
              }
            }
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            if (payload.old && onDeleteRef.current) {
              onDeleteRef.current(payload.old);
            }
          },
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            isSubscribed.current = true;
          } else if (status === 'CLOSED') {
            isSubscribed.current = false;
          }
        });

      channelRef.current = channel;

      return () => {
        isSubscribed.current = false;
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error setting up realtime subscription:', error);
      isSubscribed.current = false;
    }
  }, [userId, type]); // Only re-run when userId or type changes — callbacks use refs

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        isSubscribed.current = false;
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);
}
